import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { BookOpen, Search, Loader2, Clock, FileText } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HIZLI_KONULAR = [
  { konu: 'İş Artışı Sınırları', detay: 'Birim fiyat ve anahtar teslim sözleşmelerde iş artışı/eksilişi sınırları ve hesaplama yöntemleri' },
  { konu: 'Süre Uzatımı', detay: 'YİGŞ Md.24 kapsamında süre uzatımı verilmesi gereken haller ve prosedür' },
  { konu: 'Gecikme Cezası', detay: 'Yapım işlerinde gecikme cezası hesaplama yöntemleri ve üst sınırları' },
  { konu: 'Fiyat Farkı', detay: 'Yapım işlerinde fiyat farkı hesabı, Bakanlar Kurulu kararı ve uygulama' },
  { konu: 'Geçici ve Kesin Kabul', detay: 'Yapım İşleri Muayene ve Kabul Yönetmeliği kapsamında kabul süreçleri' },
  { konu: 'Teminat İadesi', detay: 'Kesin teminat ve ek kesin teminat iade koşulları ve süreleri' },
];

export default function ISYMevzuat() {
  const [konu, setKonu] = useState('');
  const [detay, setDetay] = useState('');
  const [loading, setLoading] = useState(false);
  const [sonuc, setSonuc] = useState(null);
  const [gecmis, setGecmis] = useState([]);
  const [showGecmis, setShowGecmis] = useState(false);

  useEffect(() => { fetchGecmis(); }, []);

  const fetchGecmis = async () => {
    try {
      const res = await axios.get(`${API}/isy/mevzuat/gecmis`, { withCredentials: true });
      setGecmis(res.data || []);
    } catch (e) { /* ignore */ }
  };

  const handleAnaliz = async (konuVal, detayVal) => {
    const k = konuVal || konu;
    if (!k.trim()) { toast.error('Konu zorunludur'); return; }
    setLoading(true);
    setSonuc(null);
    try {
      const res = await axios.post(`${API}/isy/mevzuat/analiz`, {
        konu: k, detay: detayVal || detay
      }, { withCredentials: true });
      setSonuc(res.data.analiz);
      setKonu(k);
      fetchGecmis();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Analiz hatası');
    }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-4" data-testid="isy-mevzuat">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-emerald-400" />
          <h2 className="text-lg font-bold text-white">Mevzuat Değişiklikleri & Analiz</h2>
          <span className="text-[10px] text-gray-500 bg-gray-800 px-2 py-0.5 rounded">Claude AI</span>
        </div>
        <Button size="sm" variant="ghost" className="text-gray-400 hover:text-white text-xs" onClick={() => setShowGecmis(!showGecmis)}>
          <Clock className="w-3.5 h-3.5 mr-1" /> Geçmiş ({gecmis.length})
        </Button>
      </div>

      {/* Hızlı Konular */}
      <div className="flex flex-wrap gap-2">
        {HIZLI_KONULAR.map((q, i) => (
          <button key={i} onClick={() => handleAnaliz(q.konu, q.detay)} disabled={loading}
            className="text-[11px] text-gray-400 bg-gray-800/60 hover:bg-gray-800 border border-gray-700/50 rounded-full px-3 py-1.5 transition-colors disabled:opacity-50" data-testid={`mevzuat-quick-${i}`}>
            <FileText className="w-3 h-3 inline mr-1" /> {q.konu}
          </button>
        ))}
      </div>

      {/* Arama Formu */}
      <Card className="bg-gray-900/80 border-gray-700/50">
        <CardContent className="pt-4 pb-3">
          <div className="flex gap-3 items-end">
            <div className="flex-1">
              <Label className="text-gray-400 text-xs">Mevzuat Konusu *</Label>
              <Input value={konu} onChange={e => setKonu(e.target.value)} placeholder="Örn: Kesin hesap düzenlenmesi"
                className="bg-gray-800 border-gray-700 text-white h-8 text-sm" data-testid="mevzuat-konu" />
            </div>
            <div className="flex-1">
              <Label className="text-gray-400 text-xs">Detay (opsiyonel)</Label>
              <Input value={detay} onChange={e => setDetay(e.target.value)} placeholder="Ek bilgi..."
                className="bg-gray-800 border-gray-700 text-white h-8 text-sm" />
            </div>
            <Button onClick={() => handleAnaliz()} disabled={loading || !konu.trim()} className="bg-emerald-600 hover:bg-emerald-700 text-white h-8" data-testid="mevzuat-analiz-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Search className="w-4 h-4 mr-1" /> Analiz Et</>}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Sonuç */}
      {loading && (
        <Card className="bg-gray-900/60 border-gray-700/50">
          <CardContent className="py-8 flex items-center justify-center gap-2">
            <Loader2 className="w-5 h-5 text-emerald-400 animate-spin" />
            <span className="text-sm text-gray-400">Mevzuat analizi yapılıyor...</span>
          </CardContent>
        </Card>
      )}

      {sonuc && !loading && (
        <Card className="bg-gray-900/60 border-emerald-800/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-emerald-400 flex items-center gap-2">
              <BookOpen className="w-4 h-4" /> Analiz Sonucu: {konu}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="prose prose-invert prose-sm max-w-none">
              <div className="text-sm text-gray-200 whitespace-pre-wrap leading-relaxed" data-testid="mevzuat-sonuc">
                {sonuc}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Geçmiş */}
      {showGecmis && gecmis.length > 0 && (
        <Card className="bg-gray-900/60 border-gray-700/50">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-gray-300">Geçmiş Analizler</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {gecmis.map(g => (
                <div key={g.id} className="p-2 bg-gray-800/40 rounded-lg cursor-pointer hover:bg-gray-800/60 transition-colors"
                  onClick={() => { setKonu(g.konu); setSonuc(g.analiz); setShowGecmis(false); }}>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-white font-medium">{g.konu}</span>
                    <span className="text-[10px] text-gray-500">{g.created_at?.split('T')[0]}</span>
                  </div>
                  {g.detay && <p className="text-[10px] text-gray-500 mt-0.5">{g.detay}</p>}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
